import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { accountMappingService } from '../../services/accounting/account-mapping';
import { chartOfAccountsService } from '../../services/accounting/chart-of-accounts';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AccountMappingModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState({
    revenue: {},
    expenses: {},
    assets: {},
    liabilities: {},
    equity: {}
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading account mappings and chart of accounts...');
      
      // Load chart of accounts and account mappings in parallel
      const [accountsData, mappingsData] = await Promise.all([
        chartOfAccountsService.getChartOfAccounts(currentCompany.id),
        accountMappingService.getAccountMappings(currentCompany.id)
      ]);
      
      setAccounts(accountsData);
      setMappings(mappingsData);
      console.log('Account mappings loaded:', mappingsData);
    } catch (error) {
      console.error('Error loading account mapping data:', error);
      toast.error('Failed to load account mapping data');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadData();
    }
  }, [isOpen, currentCompany?.id, loadData]);

  const handleMappingChange = (category, type, accountCode) => {
    setMappings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: accountCode
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Saving account mappings:', mappings);
      
      await accountMappingService.updateAccountMappings(
        currentCompany.id,
        mappings,
        user.uid
      );
      
      toast.success('Account mappings saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving account mappings:', error);
      toast.error('Failed to save account mappings');
    } finally {
      setSaving(false);
    }
  };

  const getAccountOptions = (accountType) => {
    // Filter accounts and remove duplicates based on accountCode
    const filteredAccounts = accounts.filter(account => account.accountType === accountType && account.isActive);
    
    // Use Map to deduplicate by accountCode, keeping the first occurrence
    const deduplicatedMap = new Map();
    filteredAccounts.forEach(account => {
      if (!deduplicatedMap.has(account.accountCode)) {
        deduplicatedMap.set(account.accountCode, account);
      }
    });
    
    return Array.from(deduplicatedMap.values())
      .sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 max-w-6xl shadow-lg rounded-lg bg-white max-h-[85vh] flex flex-col mb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {t('accounting.account_mappings', 'Account Mappings')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('accounting.account_mappings_desc', 'Configure which accounts are used for different types of transactions')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              <p className="mt-4 text-gray-500">{t('accounting.loading_mappings', 'Loading account mappings...')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto space-y-6 pb-4">
              {/* Revenue Accounts */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                  <span className="w-3 h-6 bg-green-500 rounded mr-3"></span>
                  {t('accounting.revenue_accounts', 'Revenue Accounts')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('accounting.logistics_revenue', 'Logistics Service Revenue')}
                    </label>
                    <select
                      value={mappings.revenue.logisticsRevenue || ''}
                      onChange={(e) => handleMappingChange('revenue', 'logisticsRevenue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select Account...</option>
                      {getAccountOptions('revenue').map(account => (
                        <option key={account.id} value={account.accountCode}>
                          {account.accountCode} - {account.accountName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('accounting.transportation_revenue', 'Transportation Revenue')}
                    </label>
                    <select
                      value={mappings.revenue.transportationRevenue || ''}
                      onChange={(e) => handleMappingChange('revenue', 'transportationRevenue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select Account...</option>
                      {getAccountOptions('revenue').map(account => (
                        <option key={account.id} value={account.accountCode}>
                          {account.accountCode} - {account.accountName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('accounting.other_revenue', 'Other Revenue')}
                    </label>
                    <select
                      value={mappings.revenue.otherRevenue || ''}
                      onChange={(e) => handleMappingChange('revenue', 'otherRevenue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select Account...</option>
                      {getAccountOptions('revenue').map(account => (
                        <option key={account.id} value={account.accountCode}>
                          {account.accountCode} - {account.accountName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Expense Accounts */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                  <span className="w-3 h-6 bg-red-500 rounded mr-3"></span>
                  {t('accounting.expense_accounts', 'Expense Accounts')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: 'fuel', label: 'Fuel Expenses' },
                    { key: 'maintenance', label: 'Vehicle Maintenance' },
                    { key: 'salary', label: 'Salary Expenses' },
                    { key: 'commission', label: 'Commission Expenses' },
                    { key: 'office', label: 'Office Supplies' },
                    { key: 'marketing', label: 'Marketing Expenses' },
                    { key: 'insurance', label: 'Insurance Expenses' },
                    { key: 'utilities', label: 'Utilities' },
                    { key: 'other', label: 'Other Expenses' }
                  ].map(expense => (
                    <div key={expense.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t(`accounting.${expense.key}_expenses`, expense.label)}
                      </label>
                      <select
                        value={mappings.expenses[expense.key] || ''}
                        onChange={(e) => handleMappingChange('expenses', expense.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">Select Account...</option>
                        {getAccountOptions('expense').map(account => (
                          <option key={account.id} value={account.accountCode}>
                            {account.accountCode} - {account.accountName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Asset Accounts */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                  <span className="w-3 h-6 bg-blue-500 rounded mr-3"></span>
                  {t('accounting.asset_accounts', 'Asset Accounts')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: 'cash', label: 'Cash' },
                    { key: 'bank', label: 'Bank Account' },
                    { key: 'accountsReceivable', label: 'Accounts Receivable' },
                    { key: 'vatReceivable', label: 'VAT Receivable' },
                    { key: 'inventory', label: 'Inventory' },
                    { key: 'fixedAssets', label: 'Fixed Assets' }
                  ].map(asset => (
                    <div key={asset.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t(`accounting.${asset.key}`, asset.label)}
                      </label>
                      <select
                        value={mappings.assets[asset.key] || ''}
                        onChange={(e) => handleMappingChange('assets', asset.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Account...</option>
                        {getAccountOptions('asset').map(account => (
                          <option key={account.id} value={account.accountCode}>
                            {account.accountCode} - {account.accountName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Liability Accounts */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-purple-800 mb-4 flex items-center">
                  <span className="w-3 h-6 bg-purple-500 rounded mr-3"></span>
                  {t('accounting.liability_accounts', 'Liability Accounts')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: 'accountsPayable', label: 'Accounts Payable' },
                    { key: 'vatPayable', label: 'VAT Payable' },
                    { key: 'accruedExpenses', label: 'Accrued Expenses' },
                    { key: 'salariesPayable', label: 'Salaries Payable' },
                    { key: 'longTermDebt', label: 'Long-term Debt' }
                  ].map(liability => (
                    <div key={liability.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t(`accounting.${liability.key}`, liability.label)}
                      </label>
                      <select
                        value={mappings.liabilities[liability.key] || ''}
                        onChange={(e) => handleMappingChange('liabilities', liability.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Select Account...</option>
                        {getAccountOptions('liability').map(account => (
                          <option key={account.id} value={account.accountCode}>
                            {account.accountCode} - {account.accountName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-4 mt-6 border-t bg-gray-50 px-5 py-4 pb-6 rounded-b-lg flex-shrink-0">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common.cancel', t('common.cancel'))}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('common.saving', 'Saving...')}</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    <span>{t('accounting.save_mappings', 'Save Mappings')}</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountMappingModal; 